import { prepareTransition } from 'util.misc'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'vendor.body-scroll-lock'
import { trapFocus, removeTrapFocus, focusable } from 'util.a11y'
import { EVENTS } from 'util.events'

class HeaderDrawer extends HTMLElement {
  constructor() {
    super()
    this.isOpen = false
  }
  connectedCallback() {
    // RareSeeds 2026-10-12: a fresh controller on EVERY connect. It used to be made once in the
    // constructor, so after any disconnect/reconnect (theme-editor section reloads re-attach the
    // header) the listeners below were registered with an already-aborted signal -- silently never
    // added -- and the hamburger did nothing until a refresh.
    this.abortController?.abort()
    this.abortController = new AbortController()
    this.isOpen = this.classList.contains('is-active')
    if (!this.getAttribute('open') || !this.getAttribute('close')) return
    document.addEventListener(this.getAttribute('open'), this.open.bind(this), { signal: this.abortController.signal })
    document.addEventListener(this.getAttribute('close'), this.close.bind(this), {
      signal: this.abortController.signal
    })
  }

  // a11y 2026-10-16 (A11Y-MANUAL-3): the drawers are aria-modal dialogs (snippets/header.drawer.liquid),
  // so the page behind them must really be inert: <main>, the footer group and the header group's
  // OTHER sections (announcement toolbar, A-Z bar). Never the header section itself -- the drawers
  // live inside it. Not a scroll lock and not an overlay: the live-parity note in open() is untouched
  // and nothing visual changes. An inert <main> is skipped by hit-testing, so an outside click still
  // lands on body and closes the drawer as before.
  static inertTargets() {
    return [
      document.getElementById('MainContent'),
      ...document.querySelectorAll('.shopify-section-group-footer-group'),
      ...document.querySelectorAll('.shopify-section-group-header-group:not(.header-section)')
    ].filter(Boolean)
  }

  setPageInert(on) {
    // another header drawer still open (cart over flyout): leave the page inert
    if (!on && document.querySelector('header-drawer.site-header__drawer.is-active')) return
    HeaderDrawer.inertTargets().forEach((el) => (on ? el.setAttribute('inert', '') : el.removeAttribute('inert')))
  }

  open(evt) {
    this.activeElement = evt.target

    this.dispatchEvent(new CustomEvent(EVENTS.sizeDrawer, { bubbles: true }))

    prepareTransition(
      this,
      function () {
        this.classList.add('is-active')
      }.bind(this)
    )

    this.setPageInert(true)

    // RareSeeds: the live site's flyout does NOT lock or scroll-jump the page —
    // skip body-scroll-lock / focus trap / scroll reset for the mobile-nav drawer.
    // The cart drawer keeps stock Expanse behavior.
    const isRsFlyout = this.classList.contains('site-header__mobile-nav')

    // Hoisted above the guard: the second !isRsFlyout block below restores these, and leaving
    // them const-scoped inside the first block made that a ReferenceError.
    const _scrollX = window.scrollX
    const _scrollY = window.scrollY
    const _prevScrollBehavior = document.documentElement.style.scrollBehavior

    if (!isRsFlyout) {
      document.documentElement.style.scrollBehavior = 'auto'

      disableBodyScroll(this, {
        reserveScrollBarGap: true,
        // Explicitly allow touch events within drawer for iOS scroll support
        allowTouchMove: (el) => this === el || this.contains(el),
      });



    }

    // Focus management applies to BOTH drawers. The live-parity note above is about SCROLL
    // behaviour -- the live flyout does not lock or scroll-jump the page -- and focus trapping got
    // swept into the same skip by accident. They are unrelated concerns: skipping the trap left the
    // mobile flyout, the only navigation on the site, as a non-dialog a keyboard user Tabs straight
    // out of and behind. Scroll-lock stays skipped for the flyout; the trap does not.
    trapFocus(this)
    this.addEventListener('keydown', this.handleKeydown, { signal: this.abortController.signal })

    if (!isRsFlyout) {
      const siteHeader = document.querySelector('#SiteHeader')
      const isSticky = siteHeader?.dataset.sticky === 'true'

      requestAnimationFrame(() => {
        if (!isSticky) {
          window.scrollTo(0, 0)
        } else {
          window.scrollTo(_scrollX, _scrollY)
        }

        document.documentElement.style.scrollBehavior = _prevScrollBehavior || ''
      })
    }

    // Esc key closes the drawer
    window.addEventListener('keyup', this.handleWindowKeyup, { signal: this.abortController.signal })

    this.dispatchEvent(new CustomEvent(EVENTS.headerOverlayRemoveClass, { bubbles: true }))
    this.dispatchEvent(new CustomEvent(EVENTS.headerDrawerOpened, { bubbles: true }))
    this.dispatchEvent(new CustomEvent('drawerOpen', { bubbles: true }))

    this.isOpen = true

    // Clicking out of the drawer closes it
    setTimeout(() => {
      window.addEventListener('click', this.handleWindowClick, { signal: this.abortController.signal })
    }, 0)
  }

  close(evt, noAnimate) {
    // Do not close if click event came from inside drawer
    if (evt && evt.target.closest && evt.target.closest('.site-header__drawer')) {
      return
    }

    if (!this.isOpen && !this.classList.contains('is-active')) {
      return
    }

    if (noAnimate) {
      this.classList.remove('is-active')

      this.dispatchEvent(new CustomEvent(EVENTS.headerDrawerClosed, { bubbles: true }))

      window.removeEventListener('keyup', this.handleWindowKeyup)
      window.removeEventListener('click', this.handleWindowClick)
      this.removeEventListener('keydown', this.handleKeydown)

      this.isOpen = false

      // inert off BEFORE focus returns: an opener inside <main> (PLP price pill, Quick Order, the
      // home header's glyph) cannot take focus while <main> is inert.
      this.setPageInert(false)
      // Remove focus trap
      if (this.activeElement) removeTrapFocus(this.activeElement)

      clearAllBodyScrollLocks()
    } else {
      // RareSeeds 2026-10-12: transitionend is not guaranteed -- background tabs pause transitions,
      // and an already-hidden drawer has nothing to transition. When it never fired, isOpen stayed
      // true and the hamburger's next tap was read as "close" forever (menu dead until refresh).
      // A timeout finishes the close regardless; the guard makes it run once.
      let finished = false
      const onClosed = () => {
        if (finished) return
        finished = true
        // reopened before the close finished: leave the open drawer alone
        if (this.classList.contains('is-active')) return
        this.removeEventListener('transitionend', onClosed)

        this.dispatchEvent(new CustomEvent(EVENTS.headerDrawerClosed, { bubbles: true }))

        window.removeEventListener('keyup', this.handleWindowKeyup)
        window.removeEventListener('click', this.handleWindowClick)
        this.removeEventListener('keydown', this.handleKeydown)

        this.isOpen = false

        // inert off BEFORE focus returns (see the noAnimate branch above)
        this.setPageInert(false)
        // Remove focus trap
        if (this.activeElement) removeTrapFocus(this.activeElement)

        clearAllBodyScrollLocks()
      }

      this.addEventListener('transitionend', onClosed, { once: true })
      setTimeout(onClosed, 700)

      prepareTransition(
        this,
        function () {
          this.classList.remove('is-active')
        }.bind(this)
      )
    }
  }

  handleWindowKeyup = (evt) => {
    if (evt.keyCode === 27) {
      this.close()
    }
  }

  handleWindowClick = (evt) => {
    this.close(evt)
  }

  handleKeydown = (evt) => {
    const isTab = evt.key === 'Tab' || evt.keyCode === 9
    if (!isTab) return

    let elements = []
    try {
      elements = focusable(this) || []
    } catch (e) {
      elements = []
    }
    // final verify 2026-10-19: hidden nodes (empty-state link, app links) counted as first/last and let Tab escape the drawer
    elements = elements.filter((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden')
    if (!elements.length) return

    const first = elements[0]
    const last = elements[elements.length - 1]
    const active = document.activeElement

    if (!evt.shiftKey && active === last) {
      evt.preventDefault()
      first && first.focus()
      return
    }

    if (evt.shiftKey && (active === first || active === this)) {
      evt.preventDefault()
      last && last.focus()
      return
    }
  }

  disconnectedCallback() {
    this.abortController.abort()
    // a11y 2026-10-16: a theme-editor re-render can remove an OPEN drawer; the page behind must not
    // stay inert. This element is already out of the document, so the guard only sees the other one.
    this.setPageInert(false)
  }
}

customElements.define('header-drawer', HeaderDrawer)
